import { apiClient } from './api.client';

export interface RoleDto {
  id: number;
  name: string;
  displayName: string;
  description: string | null;
  isSystem: boolean;
  companyId: number | null;
  isActive: boolean;
  rank: number;
  color: string | null;
  icon: string | null;
  /** Actual-mode grant count. */
  permissionCount: number;
  /** Compliance-mode grant count (isolated set). */
  compliancePermissionCount: number;
  userCount: number;
}

export type PermissionMode = 'Actual' | 'Compliance';

export interface RoleDetailDto extends RoleDto {
  /** Actual-mode grants (legacy shape). */
  permissions: string[];
  /** Grants per login mode — two isolated sets. */
  permissionsByMode: Record<PermissionMode, string[]>;
}

/** Mode → Module → Submodule → Action tree (built server-side from the seeded catalogue). */
export interface PermissionTreeAction { id: number; name: string; action: string; displayName: string; description: string | null; }
export interface PermissionTreeSubmodule { submodule: string; group: string; actions: PermissionTreeAction[]; }
export interface PermissionTreeModule { module: string; totalPermissions: number; submodules: PermissionTreeSubmodule[]; }
export interface PermissionTreeMode { mode: PermissionMode; totalPermissions: number; modules: PermissionTreeModule[]; }
export interface PermissionTree { modes: PermissionTreeMode[]; totalPermissions: number; }

export interface PermissionDto {
  id: number;
  name: string;
  displayName: string;
  module: string;
  description: string | null;
}

export interface PermissionGroupDto {
  module: string;
  permissions: PermissionDto[];
}

export const permissionService = {
  async getMyPermissions(): Promise<string[]> {
    const res = await apiClient.get<string[]>('/auth/permissions');
    return res.data;
  },
};

export const roleService = {
  async getAll(): Promise<RoleDto[]> {
    const res = await apiClient.get<RoleDto[]>('/roles');
    return res.data;
  },

  async getAssignable(): Promise<RoleDto[]> {
    const res = await apiClient.get<RoleDto[]>('/roles/assignable');
    return res.data;
  },

  async getById(id: number): Promise<RoleDetailDto> {
    const res = await apiClient.get<RoleDetailDto>(`/roles/${id}`);
    return res.data;
  },

  async create(data: { name: string; displayName: string; description?: string }): Promise<RoleDto> {
    const res = await apiClient.post<RoleDto>('/roles', data);
    return res.data;
  },

  async update(id: number, data: { name: string; displayName: string; description?: string }): Promise<RoleDto> {
    const res = await apiClient.put<RoleDto>(`/roles/${id}`, data);
    return res.data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`/roles/${id}`);
  },

  /** Replaces ONE mode's isolated grant set for the role. */
  async setPermissions(roleId: number, permissionNames: string[], mode: PermissionMode = 'Actual'): Promise<void> {
    await apiClient.put(`/roles/${roleId}/permissions`, { mode, permissionNames });
  },

  async getPermissionTree(): Promise<PermissionTree> {
    const res = await apiClient.get<PermissionTree>('/permissions/tree');
    return res.data;
  },

  async getAllPermissions(): Promise<PermissionGroupDto[]> {
    const res = await apiClient.get<PermissionGroupDto[]>('/permissions');
    return res.data;
  },

  async getUserRoles(userId: number): Promise<RoleDto[]> {
    const res = await apiClient.get<RoleDto[]>(`/users/${userId}/roles`);
    return res.data;
  },

  async setUserRoles(userId: number, roleIds: number[]): Promise<void> {
    await apiClient.put(`/users/${userId}/roles`, roleIds);
  },
};
